/**
 * useCompetitorTools — React Query hooks for Competitor Intelligence.
 *
 * Covers:
 * - useLinkPolicy: fetch Never Use / Do Use domain lists
 * - useAddToPolicy: add domain to a policy list (with conflict detection)
 * - useRemoveFromPolicy: remove a policy entry
 * - useSERPAnalysis: analyse SERP for a keyword
 *
 * NOTE: Mutations use direct useState instead of useMutation to ensure
 * state updates are synchronous within React's act() boundary in tests.
 */

import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { competitorService } from "@/services/competitorService";
import type { LinkPolicyEntry } from "@/services/competitorService";

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const competitorKeys = {
	linkPolicy: (tenantId: string, policyType?: string) =>
		["link-policy", tenantId, policyType] as const,
	serpAnalysis: (keyword: string, tenantId: string) =>
		["serp-analysis", keyword, tenantId] as const,
};

// ---------------------------------------------------------------------------
// useLinkPolicy — fetch policy entries
// ---------------------------------------------------------------------------

export const useLinkPolicy = (
	tenantId: string | null,
	policyType?: "never_use" | "do_use",
) => {
	const query = useQuery({
		queryKey: competitorKeys.linkPolicy(tenantId ?? "", policyType),
		queryFn: () =>
			competitorService.getLinkPolicy(tenantId!, policyType),
		enabled: !!tenantId,
		select: (data) => data.entries,
	});

	return {
		...query,
		entries: query.data ?? [],
	};
};

// ---------------------------------------------------------------------------
// useAddToPolicy — mutation with conflict tracking
// Using useState for synchronous state in test environments (act() compatible)
// ---------------------------------------------------------------------------

interface AddToPolicyVars {
	tenantId: string;
	domain: string;
	policyType: "never_use" | "do_use";
	name?: string;
}

type MutationStatus = "idle" | "pending" | "success" | "error";

export const useAddToPolicy = () => {
	const queryClient = useQueryClient();
	const [status, setStatus] = useState<MutationStatus>("idle");
	const [data, setData] = useState<LinkPolicyEntry | undefined>(undefined);
	const [error, setError] = useState<unknown>(null);
	const [conflictEntry, setConflictEntry] = useState<
		LinkPolicyEntry | undefined
	>(undefined);

	const mutateAsync = useCallback(
		async (vars: AddToPolicyVars): Promise<LinkPolicyEntry> => {
			setStatus("pending");
			setError(null);
			try {
				const result = await competitorService.addToPolicy({
					tenant_id: vars.tenantId,
					domain: vars.domain,
					policy_type: vars.policyType,
					name: vars.name,
				});
				setData(result);
				setConflictEntry(undefined);
				setStatus("success");
				queryClient.invalidateQueries({ queryKey: ["link-policy"] });
				return result;
			} catch (err) {
				// Extract conflict entry from 409 response
				const axiosError = err as {
					response?: {
						status: number;
						data?: { existing_entry?: LinkPolicyEntry };
					};
				};
				if (axiosError?.response?.status === 409) {
					const entry = axiosError.response?.data?.existing_entry;
					if (entry) setConflictEntry(entry as LinkPolicyEntry);
				}
				setError(err);
				setStatus("error");
				throw err;
			}
		},
		[queryClient],
	);

	const mutate = useCallback(
		(vars: AddToPolicyVars) => {
			mutateAsync(vars).catch(() => {});
		},
		[mutateAsync],
	);

	return {
		data,
		error,
		conflictEntry,
		isIdle: status === "idle",
		isPending: status === "pending",
		isSuccess: status === "success",
		isError: status === "error",
		mutate,
		mutateAsync,
	};
};

// ---------------------------------------------------------------------------
// useRemoveFromPolicy — mutation
// ---------------------------------------------------------------------------

interface RemoveFromPolicyVars {
	tenantId: string;
	entryId: string;
}

export const useRemoveFromPolicy = () => {
	const queryClient = useQueryClient();
	const [status, setStatus] = useState<MutationStatus>("idle");
	const [error, setError] = useState<unknown>(null);

	const mutateAsync = useCallback(
		async (vars: RemoveFromPolicyVars): Promise<void> => {
			setStatus("pending");
			setError(null);
			try {
				await competitorService.removeFromPolicy(vars.entryId, vars.tenantId);
				setStatus("success");
				queryClient.invalidateQueries({ queryKey: ["link-policy"] });
			} catch (err) {
				setError(err);
				setStatus("error");
				throw err;
			}
		},
		[queryClient],
	);

	const mutate = useCallback(
		(vars: RemoveFromPolicyVars) => {
			mutateAsync(vars).catch(() => {});
		},
		[mutateAsync],
	);

	return {
		error,
		isIdle: status === "idle",
		isPending: status === "pending",
		isSuccess: status === "success",
		isError: status === "error",
		mutate,
		mutateAsync,
	};
};

// ---------------------------------------------------------------------------
// useSERPAnalysis — query with derived state
// ---------------------------------------------------------------------------

interface SERPAnalysisParams {
	keyword: string;
	tenantId: string | null;
}

export const useSERPAnalysis = ({ keyword, tenantId }: SERPAnalysisParams) => {
	const query = useQuery({
		queryKey: competitorKeys.serpAnalysis(keyword, tenantId ?? ""),
		queryFn: () => competitorService.analyzeSERP(keyword, tenantId!),
		enabled: !!keyword && !!tenantId,
	});

	const data = query.data;

	return {
		...query,
		// Convenience flags derived from response data
		isCached: data?.from_cache ?? false,
		cachedAt: data?.from_cache ? data.analyzed_at : undefined,
		isDisabled: !!data?.error_message,
		disabledReason: data?.error_message ?? undefined,
	};
};

// ---------------------------------------------------------------------------
// Saved Gaps
// ---------------------------------------------------------------------------

export type GapContentStatus = "not_started" | "in_progress" | "draft" | "published" | "failed";

export interface SavedGap {
	id: string;
	topic: string;
	created_at: string | null;
	volume: number;
	kd: number;
	cpc: number;
	intent: string;
	potential_traffic: number;
	opportunity_reason: string | null;
	enriched: boolean;
	content_id: string | null;
	content_status: GapContentStatus;
	content_url: string | null;
	content_title: string | null;
}

export function useSavedGaps(tenantId: string | null) {
	return useQuery<SavedGap[]>({
		queryKey: ["competitor", "gaps", tenantId],
		queryFn: async () => {
			if (!tenantId) return [];
			const { apiClient } = await import("@/services/apiClient");
			const res = await apiClient.get("/content-planner/gaps", {
				params: { tenant_id: tenantId },
			});
			return res.data.gaps ?? [];
		},
		enabled: !!tenantId,
		staleTime: 30_000,
	});
}

export function useDeleteGap(tenantId: string | null) {
	const qc = useQueryClient();
	return useCallback(
		async (gapId: string) => {
			const { apiClient } = await import("@/services/apiClient");
			await apiClient.delete(`/content-planner/gaps/${gapId}`, {
				params: { tenant_id: tenantId },
			});
			qc.invalidateQueries({ queryKey: ["competitor", "gaps", tenantId] });
		},
		[tenantId, qc],
	);
}

export function useEnrichGaps(tenantId: string | null) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ force = false }: { force?: boolean } = {}) => {
			const { apiClient } = await import("@/services/apiClient");
			const res = await apiClient.post("/content-planner/gaps/enrich", null, {
				params: { tenant_id: tenantId, force },
			});
			return res.data as { enriched: number; total: number; errors: number };
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["competitor", "gaps", tenantId] });
		},
	});
}
